import React, { useEffect, useRef, useState, useCallback } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { ClientId, NamesOfFieldEnum } from "~/typings/types";
import { RowFieldWithButtonsContainer } from "../form-field";
import styles from "./PayformNameField.module.scss";
import { ErrorField, TitleField } from "~/components";
import { ButtonIcon } from "~/components/icon-button";
import { useIsMobile } from "~/hooks";
import cn from "classnames";
import { List, message } from "antd";
import { composeValidators, required } from "~/utils";
import { getGeneratedDomains } from "../../v2/customer/api/apiMethods";
import { ERROR_FIELD_ } from "~/configs/consts";

const PAYFORM_SUFFIX = ".payform.ru";

// Хелпер — убираем протокол и суффикс
function normalizeDomain(domain: string) {
  if (!domain) return "";
  return domain
    .replace(/^https?:\/\//i, "")
    .replace(new RegExp(`${PAYFORM_SUFFIX}$`, "i"), "");
}

function getDisplayDomain(domain: string) {
  return `https://${domain}${PAYFORM_SUFFIX}`;
}

interface PayformNameFieldProps {
  titleField?: string;
  name: NamesOfFieldEnum;
  required?: boolean;
  tooltip?: string;
  typeRequestCheckPayformName: string;
  setTypeRequestCheckPayformName: (type: string) => void;
  errorMsgCheckPayformName?: string;
  onBlurCallback?: (value: string) => void;
  clientId: ClientId;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
}

export const PayformNameField = ({
  clientId,
  typeRequestCheckPayformName,
  setTypeRequestCheckPayformName,
  prefix = "https://",
  postfix = ".payform.ru",
  ...props
}: PayformNameFieldProps) => {
  const isMobile = useIsMobile();
  const form = useForm();
  const { values } = useFormState();

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputBlocked, setInputBlocked] = useState(false);
  const [hasFocusedOnce, setHasFocusedOnce] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const [showEmptyResult, setShowEmptyResult] = useState(false);
  const [inputChangedAfterEmpty, setInputChangedAfterEmpty] = useState(false);
  const [payloadAtEmptyResult, setPayloadAtEmptyResult] = useState<{ product_type: string; product_name: string; product_description: string } | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const suggestionsRef = useRef<HTMLDivElement>(null);
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isLoading = typeRequestCheckPayformName === "loading";

  const payloadRequest = {
    product_type: values?.product_type || "",
    product_name: values?.product_name || "",
    product_description: values?.product_description || ""
  };

  const hasPayload = Object.values(payloadRequest).some(Boolean);
  const allFieldsFilled = Object.values(payloadRequest).every(field => field.trim() !== "");

  const hasFieldsChangedSinceEmpty =
    payloadAtEmptyResult !== null && (
      payloadRequest.product_name !== payloadAtEmptyResult.product_name ||
      payloadRequest.product_description !== payloadAtEmptyResult.product_description ||
      payloadRequest.product_type !== payloadAtEmptyResult.product_type
    );

  const getTooltipText = () => {
    if (allFieldsFilled) return 'Сгенерировать';

    const emptyFields = [];
    if (!values?.product_type) emptyFields.push('«Что продаёте?»');
    if (!values?.product_name) emptyFields.push('«Название вашего проекта');
    if (!values?.product_description) emptyFields.push('«Описание вашей деятельности»');

    return `Для генерации вариантов URL необходимо заполнить поля: ${emptyFields.join(', ')}`;
  };


  const fetchDomains = useCallback(async () => {
    try {
      if (!allFieldsFilled) {
        const emptyFields = [];
        if (!values?.product_type) emptyFields.push('«Что продаёте?»');
        if (!values?.product_name) emptyFields.push('«Название вашего проекта');
        if (!values?.product_description) emptyFields.push('«Описание вашей деятельности»');

        message.warning(`Для генерации вариантов необходимо заполнить поля: ${emptyFields.join(', ')}`)
        return;
      }
      setTypeRequestCheckPayformName('loading');
      setShowEmptyResult(false);
      setInputChangedAfterEmpty(false);
      const res = await getGeneratedDomains({ id: clientId, ...payloadRequest });
      const rawDomains: string[] = res?.data?.data || [];
      const clean = rawDomains.map(normalizeDomain).filter(Boolean);
      if (clean.length === 0) {
        setHasGeneratedOnce(true);
        setShowEmptyResult(true);
        setInputChangedAfterEmpty(false);
        setPayloadAtEmptyResult(payloadRequest);
        setShowSuggestions(false);
      } else {
        setSuggestions(clean);
        setShowSuggestions(true);
        setShowEmptyResult(false);
        setHasGeneratedOnce(true);
      }
    } catch (e) {
      console.error("Ошибка при генерации доменов:", e);
    } finally {
      setTypeRequestCheckPayformName('init');
    }
  }, [clientId, payloadRequest]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      suggestionsRef.current &&
      !suggestionsRef.current.contains(event.target as Node)
    ) {
      setShowSuggestions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleFocus = async () => {
    const currentValue = values?.[NamesOfFieldEnum.PayformName];
    setHasFocusedOnce(true);

    if (!currentValue && allFieldsFilled) {
      await fetchDomains();
    }
    setIsFocused(true);
  };

  const handleSelectSuggestion = (value: string) => {
    setShowSuggestions(false);
    form.change(NamesOfFieldEnum.PayformName, normalizeDomain(value));
  };

  return (
    <div className={styles.container}>
      <TitleField
        name={props.titleField}
        tooltip={props.tooltip}
        styleText={{ fontSize: 15 }}
        required
      />
      <div className={styles.containerContent}>
        <RowFieldWithButtonsContainer
          fields={[
            <Field
              key="payformNameInput"
              name={props.name}
              validate={composeValidators(props.required ? required() : undefined)}
            >
              {({ input, meta }) => {
                const hasError =
                  typeRequestCheckPayformName === "error" ||
                  typeRequestCheckPayformName === "errorValidateFromApi" ||
                  ((meta?.error || meta?.submitError) && meta?.submitFailed);
                const inputId = hasError ? `${ERROR_FIELD_}${input.name}` : undefined;

                return (
                  <div style={{ position: "relative", width: "100%" }}>
                    <div className={styles.inputWrapper}>
                      {prefix && <span className={styles.prefix}>{prefix}</span>}
                      <input
                        {...input}
                        id={inputId}
                        onChange={(e) => {
                          input.onChange(e);
                          if (showEmptyResult) setInputChangedAfterEmpty(true);
                        }}
                        onFocus={handleFocus}
                        onBlur={(e) => {
                          if (!input.value) {
                            return;
                          }
                          input.onBlur(e);
                          setIsFocused(false);
                          props.onBlurCallback?.(input.value);
                        }}
                        disabled={inputBlocked}
                        className={cn(styles.customInput, {
                          [styles.focusedInput]: isFocused,
                          [styles.hasPrefix]: !!prefix,
                          [styles.hasPostfix]: !!postfix,
                          [styles.inputCustomError]: meta.touched && meta.error,
                        })}
                      />
                      {postfix && <span className={styles.postfix}>{postfix}</span>}
                    </div>

                    {showSuggestions && suggestions.length > 0 && (
                      <div
                        ref={suggestionsRef}
                        style={{
                          marginTop: 4,
                          width: "100%",
                          maxHeight: 150,
                          overflowY: "auto",
                          backgroundColor: "white",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                          zIndex: 1000,
                        }}
                      >
                        <List
                          dataSource={suggestions}
                          renderItem={(suggestion, index) => (
                            <List.Item
                              key={suggestion}
                              ref={(el) => (listItemRefs.current[index] = el)}
                              onClick={() => handleSelectSuggestion(suggestion)}
                              className={
                                focusedIndex === index
                                  ? styles.focusedListItem
                                  : styles.listItem
                              }
                              onMouseEnter={() => setFocusedIndex(index)}
                            >
                              {getDisplayDomain(suggestion)}
                            </List.Item>
                          )}
                        />
                      </div>
                    )}

                    <ErrorField
                      name={props.name}
                      renderError={props?.errorMsgCheckPayformName ? {
                        has: 1,
                        message: props.errorMsgCheckPayformName
                      } : undefined}
                      descriptionField={
                        hasGeneratedOnce
                          ? "На основе заполненной информации мы автоматически сгенерировали ссылку на вашу платёжную страницу. Вы можете сгенерировать новую по зелёной кнопке рядом или написать свой вариант."
                          : ""
                      }
                    />
                  </div>
                )
              }}
            </Field>
          ]}
          buttons={[
            <ButtonIcon
              key="generateBtn"
              iconName="reload"
              variant="success"
              tooltip={getTooltipText()}
              size={isMobile ? "xs" : "sm"}
              onClick={fetchDomains}
              loading={isLoading}
              disabled={isLoading}
            />
          ]}
        />
      </div>

      {showEmptyResult && hasGeneratedOnce && !inputChangedAfterEmpty && (
        <div className={styles.emptyResult}>
          <div className={styles.emptyResultHeader}>
            <span className={styles.emptyResultIcon}>✕</span>
            <div>
              <div className={styles.emptyResultTitle}>Не получилось подобрать домен</div>
              <div className={styles.emptyResultSubtitle}>
                Нам не хватило деталей, чтобы предложить варианты. Уточните ответы ниже — и попробуем снова.
              </div>
            </div>
          </div>

          <div className={styles.emptyResultSection}>НА ЧТО ОБРАТИТЬ ВНИМАНИЕ</div>

          <div className={styles.emptyResultFields}>
            {[
              { id: "product_name", label: "Название для клиентов", hint: "Слишком общее — добавьте название бренда или формат", example: "Например: «Академия Excel», «Доставка цветов»" },
              { id: "product_description", label: "Описание продукта", hint: "Укажите: что продаёте, для кого, в каком формате", example: "Например: «курсы Excel для бухгалтеров онлайн»" },
            ].map((field, index) => (
              <div
                key={field.id}
                className={styles.emptyResultField}
                onClick={() => {
                  const els = document.querySelectorAll(`[data-field-id="${field.id}"], [name="${field.id}"]`);
                  const el = els[0] as HTMLElement | undefined;
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.focus();
                  }
                }}
              >
                <div className={styles.emptyResultFieldLeft}>
                  <span className={styles.emptyResultFieldNum}>{index + 1}</span>
                  <div>
                    <div className={styles.emptyResultFieldLabel}>{field.label}</div>
                    <div className={styles.emptyResultFieldHint}>{field.hint}</div>
                    <div className={styles.emptyResultFieldExample}>{field.example}</div>
                    <div className={styles.emptyResultFieldAction}>Нажмите, чтобы исправить →</div>
                  </div>
                </div>
                <span className={styles.emptyResultFieldArrow}>›</span>
              </div>
            ))}
          </div>

          <div className={styles.emptyResultFooter}>
            <div className={styles.emptyResultBtnRetryWrapper}>
              <button
                type="button"
                className={styles.emptyResultBtnRetry}
                onClick={fetchDomains}
                disabled={isLoading || !hasFieldsChangedSinceEmpty}
              >
                ↺ Попробовать снова
              </button>
              {!hasFieldsChangedSinceEmpty && (
                <span className={styles.emptyResultRetryTooltip}>
                  Станет доступно после изменений в полях выше
                </span>
              )}
            </div>
            <span className={styles.emptyResultManualHint}>
              или введите домен вручную в поле выше
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
